// Named call pickup groups — 3CX parity feature.
// Admin defines groups of extensions. Any group member can dial *20
// to intercept a ringing call from any other member.

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X, PhoneIncoming, Users } from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

async function fetchJson(url: string, opts?: RequestInit) {
  const res = await fetch(url, { ...opts, headers: { ...auth().headers, ...(opts?.headers || {}) } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || res.statusText);
  }
  return res.json();
}

export default function PickupGroups() {
  const me = api.me();
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<any | null>(null);

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['pickup-groups', me?.domain_uuid],
    queryFn: () => me?.domain_uuid
      ? fetchJson(`/api/pickup-groups?domain_uuid=${me.domain_uuid}`)
      : Promise.resolve([]),
    enabled: !!me?.domain_uuid,
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/pickup-groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pickup-groups', me?.domain_uuid] }),
    onError: (e: any) => toast('error', e?.message || 'Could not delete group'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <PhoneIncoming size={22} />
          Pickup Groups
        </h1>
        <button className="btn-primary" onClick={() => setEditing({})}>
          <Plus size={16} className="inline mr-1" /> New Group
        </button>
      </div>

      <div className="card p-3 text-sm text-textSecondary">
        Members of a pickup group can dial <span className="font-mono font-semibold">*20</span> to
        answer any ringing call destined for another member. Matches 3CX named pickup groups.
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Group Name</th>
              <th>Dial Code</th>
              <th>Members</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g: any) => (
              <tr
                key={g.pickup_group_uuid}
                className="hover:bg-elevated cursor-pointer"
                onClick={() => setEditing(g)}
              >
                <td>
                  <div className="font-medium">{g.group_name}</div>
                </td>
                <td>
                  <span className="font-mono text-primary">{g.group_extension || '*20'}</span>
                </td>
                <td>
                  <span className="inline-flex items-center gap-1 text-sm">
                    <Users size={13} className="text-textSecondary" />
                    {g.member_count ?? 0}
                  </span>
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <button
                    className="btn-ghost p-1 text-danger"
                    title="Delete group"
                    onClick={() => confirm(`Delete group "${g.group_name}"?`) && remove.mutate(g.pickup_group_uuid)}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!isLoading && groups.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-8 text-textSecondary">
                  No pickup groups yet. Create one to enable *20 group pickup.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing !== null && me?.domain_uuid && (
        <EditModal
          domain_uuid={me.domain_uuid}
          group={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function EditModal({ domain_uuid, group, onClose }: { domain_uuid: string; group: any; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isNew = !group.pickup_group_uuid;

  const [form, setForm] = useState({
    group_name: group.group_name || '',
    group_extension: group.group_extension || '*20',
  });
  const [memberUuids, setMemberUuids] = useState<string[]>([]);
  const [membersInitialized, setMembersInitialized] = useState(isNew);

  // Load existing members when editing an existing group
  const { data: existingMembers } = useQuery<any[]>({
    queryKey: ['pickup-group-members', group.pickup_group_uuid],
    queryFn: () => fetchJson(`/api/pickup-groups/${group.pickup_group_uuid}/members`),
    enabled: !isNew && !!group.pickup_group_uuid,
  });

  // Seed checkbox state once the member list arrives (v5 pattern — no onSuccess)
  useEffect(() => {
    if (!membersInitialized && existingMembers) {
      setMemberUuids(existingMembers.map((m: any) => m.extension_uuid));
      setMembersInitialized(true);
    }
  }, [existingMembers, membersInitialized]);

  // Extension list for the member picker
  const { data: extensions = [] } = useQuery<any[]>({
    queryKey: ['extensions-for-pg', domain_uuid],
    queryFn: () => fetchJson(`/api/tenants/${domain_uuid}/extensions`),
    enabled: !!domain_uuid,
  });

  const save = useMutation({
    mutationFn: () => {
      if (isNew) {
        return fetchJson('/api/pickup-groups', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...form, domain_uuid, member_uuids: memberUuids }),
        });
      }
      return fetchJson(`/api/pickup-groups/${group.pickup_group_uuid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, member_uuids: memberUuids }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pickup-groups', domain_uuid] });
      onClose();
    },
    onError: (e: any) => toast('error', e?.message || 'Could not save group'),
  });

  const setField = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  function toggleMember(uuid: string) {
    setMemberUuids(prev =>
      prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-lg w-full max-h-[90vh] overflow-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{isNew ? 'New' : 'Edit'} Pickup Group</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>

        <Field label="Group name *">
          <input
            className="input"
            placeholder="e.g. Front Desk"
            value={form.group_name}
            onChange={e => setField('group_name', e.target.value)}
          />
        </Field>

        <Field
          label="Dial code"
          hint="The code members dial to pick up a ringing call. Default *20 means any member dials *20."
        >
          <input
            className="input font-mono"
            value={form.group_extension}
            onChange={e => setField('group_extension', e.target.value)}
          />
        </Field>

        <div className="space-y-1">
          <label className="text-sm font-medium">Members</label>
          <div className="text-xs text-textSecondary mb-2">
            Select which extensions are in this pickup group.
          </div>
          <div className="border border-border rounded-md max-h-52 overflow-auto divide-y divide-border">
            {extensions.length === 0 && (
              <div className="text-center py-6 text-textSecondary text-sm">Loading extensions...</div>
            )}
            {extensions.map((e: any) => {
              const checked = memberUuids.includes(e.extension_uuid);
              return (
                <label
                  key={e.extension_uuid}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-elevated ${checked ? 'bg-primary/5' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleMember(e.extension_uuid)}
                    className="shrink-0"
                  />
                  <span className="font-mono text-sm text-primary mr-1">{e.extension}</span>
                  <span className="text-sm text-textPrimary truncate">
                    {[e.directory_first_name, e.directory_last_name].filter(Boolean).join(' ') || 'Unnamed'}
                  </span>
                </label>
              );
            })}
          </div>
          {memberUuids.length > 0 && (
            <div className="text-xs text-textSecondary pt-1">
              {memberUuids.length} member{memberUuids.length === 1 ? '' : 's'} selected
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!form.group_name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
      {hint && <div className="text-xs text-textSecondary">{hint}</div>}
    </div>
  );
}
